
import type {
  CustomerDetailApi,
  CustomerAgingApi,
  InvoiceApi,
  PaymentApi,
  CommunicationApi,
  ContactApi,
  CustomerAddressApi,
} from "@/types/api/customer-detail";
import { decodeHtmlEntities } from "@/lib/mappers/templates";
import type {
  CustomerDetailUI,
  CustomerAgingUI,
  CustomerAgingBandUI,
  InvoiceUI,
  PaymentUI,
  CommunicationUI,
  ContactUI,
  CustomerAddressUI,
} from "@/types/ui/customer-detail";
import type { AgingBandConfig } from "@/types/ageing-analysis";
import type { RiskLevelCode } from "@/lib/risk-level";

function mapCurrency(currency: string): "USD" | "EUR" | "GBP" | "CAD" | "AUD" {
  const normalized = currency.toUpperCase();
  if (["USD", "EUR", "GBP", "CAD", "AUD"].includes(normalized)) {
    return normalized as "USD" | "EUR" | "GBP" | "CAD" | "AUD";
  }
  return "USD"; // Default fallback
}

export function mapCustomerDetailApiToUi(api: CustomerDetailApi): CustomerDetailUI {
  return {
    id: api.id,
    name: api.name,
    accountNumber: api.accountNumber,
    currency: mapCurrency(api.currency),
    outstanding: api.outstanding,
    overdue: api.overdue,
    creditLimit: api.creditLimit,
    currencyCreditLimit: api.currencyCreditLimit,
    dso: api.dso ?? 0,
    risk: api.risk as RiskLevelCode,
    lastPayment: api.lastPayment || "",
    contact: api.contact,
    phone: api.phone,
    address: api.address,
    primaryAddressLine: api.primaryAddressLine,
    primaryAddressTown: api.primaryAddressTown,
    primaryAddressCounty: api.primaryAddressCounty,
    primaryAddressPostCode: api.primaryAddressPostCode,
    primaryAddressCountry: api.primaryAddressCountry,
    paymentTerms: api.paymentTerms || "",
    collectionStrategyId: api.collectionStrategyId || "",
    creditControllerId: api.creditControllerId || "",
    creditControllerName: api.creditControllerName,
    creditControllerEmail: api.creditControllerEmail,
    accountManagerId: api.accountManagerId,
    primaryContactFirstName: api.primaryContactFirstName,
    primaryContactSurname: api.primaryContactSurname,
    riskLevelId: api.riskLevelId,
    riskLevelName: api.riskLevelName,
    onStop: api.onStop ?? false,
    status: api.status || "Active",
    userSortKey1: api.userSortKey1 || "",
    userSortKey2: api.userSortKey2 || "",
    userSortKey3: api.userSortKey3 || "",
    userSortKey4: api.userSortKey4 || "",
    userSortKey1Label: api.userSortKey1Label || "User Sort Key 1",
    userSortKey2Label: api.userSortKey2Label || "User Sort Key 2",
    userSortKey3Label: api.userSortKey3Label || "User Sort Key 3",
    userSortKey4Label: api.userSortKey4Label || "User Sort Key 4",
    dueDays: api.dueDays,
    dueDaysType: api.dueDaysType,
    groupDso: api.groupDso,
    isAnyCurrency: api.isAnyCurrency,
    transactionCurrencies: api.transactionCurrencies,
  };
}

export function mapCustomerAgingApiToUi(api: CustomerAgingApi, agingBands: AgingBandConfig[]): CustomerAgingUI {
  const apiBandMap = new Map(api.bands.map((x) => [x.sequence, x]));

  const bands: CustomerAgingBandUI[] = agingBands.map((cfg) => {
    const apiBand = apiBandMap.get(cfg.sequence);
    return {
      sequence: cfg.sequence,
      label: apiBand?.label ?? cfg.label,
      bandLabel: apiBand?.bandLabel ?? cfg.bandLabel,
      startDays: cfg.startDays,
      endDays: cfg.endDays,
      totalOutstanding: apiBand?.totalOutstanding ?? 0,
      color: cfg.color,
    };
  });

  return {
    bands,
    total: api.totalOutstanding,
    overdue: api.totalOverdue,
  };
}

export function mapInvoiceApiToUi(api: InvoiceApi): InvoiceUI {
  return {
    id: api.invoiceNumber,
    date: api.date,
    dueDate: api.dueDate,
    amount: api.amount,
    status: api.status,
  };
}

export function mapPaymentApiToUi(api: PaymentApi): PaymentUI {
  return {
    id: api.paymentNumber,
    date: api.date,
    amount: api.amount,
    method: api.method,
    reference: api.reference,
    allocations: api.allocations.map((allocation) => ({
      invoiceId: allocation.invoiceNumber,
      invoiceDate: allocation.invoiceDate,
      amount: allocation.amount,
    })),
  };
}

export function mapCommunicationApiToUi(api: CommunicationApi): CommunicationUI {
  return {
    id: api.id,
    customerId: api.customerId,
    customerName: api.customerName,
    contactName: api.contactName,
    type: api.type,
    subject: decodeHtmlEntities(api.subject ?? ""),
    content: api.content,
    status: api.status,
    direction: api.direction,
    source: api.source,
    createdAt: api.createdAt,
    createdBy: api.createdBy,
    metadata: api.metadata,
  };
}

export function mapContactApiToUi(api: ContactApi): ContactUI {
  return {
    id: api.id,
    title: api.title,
    firstName: api.firstName,
    surname: api.surname,
    email: api.email,
    phone: api.phone,
    mobile: api.mobile,
    preferredContactMethod: api.preferredContactMethod,
    jobTitle: api.jobTitle || '',
    company: api.company,
    customerId: api.customerId,
    address: api.address || '',
    notes: api.notes,
    createdAt: api.createdAt,
    lastContact: api.lastContact,
    isPrimary: api.isPrimary,
  };
}

export function mapCustomerAddressApiToUi(api: CustomerAddressApi): CustomerAddressUI {
  return {
    id: api.id,
    customerId: api.customerId,
    externalId: api.externalId,
    accountCode: api.accountCode,
    contactName: api.contactName,
    email: api.email,
    phone: api.phone,
    fax: api.fax,
    addressLine: api.addressLine,
    town: api.town,
    county: api.county,
    postCode: api.postCode,
    country: api.country,
    countryCode: api.countryCode,
    isInvoice: api.isInvoice,
    isStatement: api.isStatement,
    isDelivery: api.isDelivery,
    isDeliveryDefault: api.isDeliveryDefault ?? false,
    isPrimary: api.isPrimary,
    contactId: api.contactId,
    notes: api.notes,
    createdAt: api.createdAt,
    updatedAt: api.updatedAt,
  };
}
